/* eslint-disable @typescript-eslint/switch-exhaustiveness-check */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { addToCartStatusChangeMilliseconds } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { CheckIcon } from '@velasca/icons/check'
import { CloseIcon } from '@velasca/icons/close'
import { ZoomPlusIcon } from '@velasca/icons/zoom-plus'
import type { Product, Status } from '@velasca/types'
import { QuickAddSizeSelectorLine } from '@velasca/ui/quick-add-size-selector-line'
import { handleError } from '@velasca/utils/handle-error'

interface QuickAddLineItemWrapperProps {
  handle: Product['handle']
  quantityAvailable: number
  onClose: () => void
  name: string
  id: string
  onAddToCart?: (handle: Product['handle']) => void
  shouldOpenCartDrawerOnAddToCart?: boolean
}

const getLineItemMessage = (translationKey?: Status): string => {
  switch (translationKey) {
    case 'loading': {
      return 'addingToCart'
    }
    case 'success': {
      return 'addedToCart'
    }
    case 'error': {
      // SHOP-2063: add error message with toast
      return 'Error'
    }
    default: {
      return 'addToCart'
    }
  }
}

export const QuickAddSizeSelectorLineWithIcons = ({
  handle,
  quantityAvailable,
  onClose,
  name,
  id,
  onAddToCart,
  shouldOpenCartDrawerOnAddToCart = false,
}: QuickAddLineItemWrapperProps) => {
  const theme = useTheme()
  const [isDrawerOpenable, setIsDrawerOpenable] = useState(false)
  const [quantity, setQuantity] = useState(quantityAvailable)
  const [quickAddState, setQuickAddState] = useState<Status>('idle')
  const { addToCart, setIsDrawerOpen } = useCart()
  const { t } = useTranslation('product')

  // Spostare in uno useEffect l'apertura del drawer fa sì che lo useScrollLock dentro MobilePopup abbia il tempo
  // di sbloccare il body.
  useEffect(() => {
    if (isDrawerOpenable) {
      setIsDrawerOpen(true)
    }
  }, [isDrawerOpenable, setIsDrawerOpen])

  const handleAddToCart = useCallback(() => {
    setQuickAddState('loading')
    addToCart(id, 1)
      .then(() => {
        setQuickAddState('success')
        setQuantity((newQuantity) => newQuantity - 1)
        setTimeout(() => {
          onClose()
          window.location.pathname !== '/cart' &&
            shouldOpenCartDrawerOnAddToCart &&
            setIsDrawerOpenable(true)
        }, addToCartStatusChangeMilliseconds)
      })
      .catch((error) => {
        setQuickAddState('error')
        handleError(error, { message: "Couldn't add product to cart." })
      })
      .finally(() => {
        onAddToCart && onAddToCart(handle)
        setTimeout(() => {
          setQuickAddState('idle')
        }, addToCartStatusChangeMilliseconds)
      })
  }, [
    addToCart,
    id,
    onClose,
    shouldOpenCartDrawerOnAddToCart,
    onAddToCart,
    handle,
  ])

  if (quantity < 1 && quickAddState === 'idle') {
    return (
      <QuickAddSizeSelectorLine
        disabled
        label={name}
        description={t('temporarilyNotAvailable')}
        key={name}
      />
    )
  }

  return (
    <QuickAddSizeSelectorLine
      label={name}
      description={t(getLineItemMessage(quickAddState))}
      key={name}
      state={quickAddState}
      onClick={handleAddToCart}
    >
      {quickAddState === 'idle' && (
        <ZoomPlusIcon width="12" height="12" fill={theme.colors.text.primary} />
      )}
      {quickAddState === 'success' && (
        <CheckIcon width="12" height="12" color={theme.colors.text.secondary} />
      )}
      {quickAddState === 'loading' && (
        <ZoomPlusIcon
          width="12"
          height="12"
          fill={theme.colors.text.secondary}
        />
      )}
      {quickAddState === 'error' && (
        <CloseIcon width="12" height="12" fill={theme.colors.text.secondary} />
      )}
    </QuickAddSizeSelectorLine>
  )
}
